import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/shared/Navbar';
import API from '../utils/api';
import { trackBlogView, trackBlogCtaClick } from '../utils/analytics';
import LeadEmailForm from '../components/LeadEmailForm';

// Strip dangerous HTML to prevent XSS — removes scripts, event handlers, and javascript: URLs.
// Used only for blog content rendered via dangerouslySetInnerHTML.
function sanitizeHtml(html) {
  return html
    // Remove <script>...</script> blocks (and self-closing variants)
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<script[^>]*\/?>/gi, '')
    // Remove on* event handlers (onclick, onload, onerror, etc.)
    .replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '')
    // Replace javascript: URLs in href/src/action attributes
    .replace(/(href|src|action)\s*=\s*(['"])\s*javascript:[^'"]*\2/gi, '$1="#"')
    // Remove <iframe> and <object> embeds
    .replace(/<(iframe|object|embed|form)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<(iframe|object|embed|form)[^>]*\/?>/gi, '');
}

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    API.get(`/blog/${slug}`)
      .then(r => {
        setPost(r.data.post);
        // Track view once per session per post
        const key = `blog_viewed_${slug}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          API.post(`/blog/${slug}/view`).catch(() => {});
          trackBlogView(slug, r.data.post.title);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleCtaClick = () => {
    const key = `blog_cta_${slug}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      API.post(`/blog/${slug}/cta`).catch(() => {});
    }
    trackBlogCtaClick(slug);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <Navbar />
      <div className="text-center py-5 mt-5">
        <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
      </div>
    </div>
  );

  if (notFound || !post) return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <Navbar />
      <div className="container text-center py-5 mt-5">
        <div style={{ fontSize: 60 }}>📭</div>
        <h2 className="fw-bold mt-3" style={{ color: 'var(--text-main)' }}>Post not found</h2>
        <Link to="/blog" className="btn btn-dream rounded-pill mt-3 px-4">Back to Blog</Link>
      </div>
    </div>
  );

  const BASE_URL   = 'https://dreamybedtime.com';
  const metaTitle  = post.metaTitle       || `${post.title} | Dreamy Bedtime`;
  const metaDesc   = post.metaDescription || post.excerpt || `Read "${post.title}" on the Dreamy Bedtime blog.`;
  const canonical  = `${BASE_URL}/blog/${post.slug}`;
  const ogImage    = post.coverImage || `${BASE_URL}/og-image.png`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: metaDesc,
    image: ogImage,
    author: { '@type': 'Organization', name: post.author || 'Dreamy Bedtime Team' },
    publisher: {
      '@type': 'Organization',
      name: 'Dreamy Bedtime',
      logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo.svg` },
    },
    datePublished: post.publishedAt,
    dateModified:  post.updatedAt,
    url: canonical,
    mainEntityOfPage: canonical,
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title"       content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url"         content={canonical} />
        <meta property="og:type"        content="article" />
        <meta property="og:image"       content={ogImage} />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:author"         content={post.author} />
        {post.tags.map(t => <meta key={t} property="article:tag" content={t} />)}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image"       content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE_URL}/blog` },
            { '@type': 'ListItem', position: 3, name: post.title, item: canonical },
          ],
        })}</script>
      </Helmet>

      <Navbar />

      {/* Cover image */}
      {post.coverImage && (
        <div style={{ height: 340, overflow: 'hidden' }}>
          <img src={post.coverImage} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}

      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">

            {/* Breadcrumb */}
            <nav className="mb-4" style={{ fontSize: '13px' }}>
              <Link to="/" className="text-muted text-decoration-none">Home</Link>
              <span className="mx-2 text-muted">/</span>
              <Link to="/blog" className="text-muted text-decoration-none">Blog</Link>
              <span className="mx-2 text-muted">/</span>
              <span style={{ color: '#6f42c1' }}>{post.title}</span>
            </nav>

            {/* Tags */}
            <div className="mb-3">
              {post.tags.map(t => (
                <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`}
                  className="badge me-2 text-decoration-none rounded-pill"
                  style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '12px', padding: '5px 12px' }}>
                  {t}
                </Link>
              ))}
            </div>

            {/* Title */}
            <h1 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(24px,4vw,40px)', lineHeight: 1.2 }}>
              {post.title}
            </h1>

            {/* Meta */}
            <div className="d-flex align-items-center gap-3 mb-4 flex-wrap" style={{ fontSize: '14px', color: 'var(--text-soft)' }}>
              <span><i className="bi bi-person me-1"></i>{post.author}</span>
              <span><i className="bi bi-calendar3 me-1"></i>{new Date(post.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <span><i className="bi bi-clock me-1"></i>{post.readingTime} min read</span>
            </div>

            {/* Excerpt */}
            {post.excerpt && (
              <p className="lead mb-5" style={{ color: 'var(--text-body)', lineHeight: 1.7, borderLeft: '4px solid #6f42c1', paddingLeft: '16px', fontStyle: 'italic' }}>
                {post.excerpt}
              </p>
            )}

            {/* Content */}
            <div
              className="blog-content"
              style={{ lineHeight: 1.85, fontSize: '17px', color: 'var(--text-body)' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }}
            />

            {/* Footer CTA */}
            <div className="mt-5 p-5 text-center rounded-4" style={{ background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)' }}>
              <div style={{ fontSize: 48 }}>🌙</div>
              <h3 className="fw-bold text-white mt-3 mb-2" style={{ fontFamily: "'Fredoka One',cursive" }}>
                Create a story for your child tonight
              </h3>
              <p className="text-white mb-4" style={{ opacity: 0.9 }}>
                Personalised bedtime stories — starring your child, their interests, and lessons they'll love.
              </p>
              <Link to="/register" className="btn btn-warning btn-lg rounded-pill fw-bold px-5" onClick={handleCtaClick}>
                Start Free — 5 Stories/Month
              </Link>
            </div>

            {/* Email lead capture — sends a free sample story */}
            <div className="mt-4 p-4 p-md-5 text-center rounded-4" style={{ background: 'var(--card-bg)', border: '1.5px solid var(--border-purple)' }}>
              <div style={{ fontSize: 36 }}>📬</div>
              <h5 className="fw-bold mt-2 mb-2" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
                Not ready to sign up? Get a free sample story
              </h5>
              <p style={{ color: 'var(--text-muted2)', fontSize: '14px', maxWidth: '440px', margin: '0 auto 18px' }}>
                We'll email you a personalised bedtime story like this one — see the magic for yourself.
              </p>
              <LeadEmailForm source="blog" askName askAge buttonLabel="📬 Send my free story" />
            </div>

            {/* Back link */}
            <div className="mt-4 text-center">
              <Link to="/blog" className="btn btn-outline-secondary rounded-pill">
                <i className="bi bi-arrow-left me-2"></i>More articles
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Blog content styles */}
      <style>{`
        .blog-content h1, .blog-content h2, .blog-content h3,
        .blog-content h4, .blog-content h5, .blog-content h6 {
          color: #2d1b69; font-family: 'Fredoka One', cursive;
          margin-top: 2rem; margin-bottom: 0.75rem; line-height: 1.3;
        }
        .blog-content h2 { font-size: 1.8rem; }
        .blog-content h3 { font-size: 1.4rem; }
        .blog-content p  { margin-bottom: 1.25rem; }
        .blog-content ul, .blog-content ol { padding-left: 1.5rem; margin-bottom: 1.25rem; }
        .blog-content li { margin-bottom: 0.4rem; }
        .blog-content blockquote {
          border-left: 4px solid #6f42c1; padding: 0.75rem 1rem;
          background: #f0ebff; border-radius: 0 8px 8px 0; margin: 1.5rem 0;
          font-style: italic; color: #4b5563;
        }
        .blog-content img { max-width: 100%; border-radius: 12px; margin: 1rem 0; }
        .blog-content a   { color: #6f42c1; text-decoration: underline; }
        .blog-content code {
          background: #f0ebff; padding: 2px 6px; border-radius: 4px;
          font-size: 0.9em; color: #6f42c1;
        }
        .blog-content pre {
          background: #1e1048; color: #c9a8ff; padding: 1rem;
          border-radius: 12px; overflow-x: auto; margin-bottom: 1.25rem;
        }
        .blog-content table { width: 100%; border-collapse: collapse; margin-bottom: 1.25rem; }
        .blog-content th, .blog-content td { border: 1px solid #e5e7eb; padding: 8px 12px; }
        .blog-content th { background: #f0ebff; color: #2d1b69; }
        .blog-content hr { border-color: #e5e7eb; margin: 2rem 0; }
      `}</style>
    </div>
  );
};

export default BlogPostPage;
